(() => {
  'use strict';

  class ReasonsWhyCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.reasons-why__track');
      this.slides = Array.from(this.querySelectorAll('.reasons-why__card'));
      this.dotsContainer = this.querySelector('.reasons-why__dots');
      this.prevBtn = this.querySelector('.reasons-why__arrow--prev');
      this.nextBtn = this.querySelector('.reasons-why__arrow--next');
      this.dots = [];

      if (!this.track || this.slides.length < 2) return;

      this.scrollScheduled = false;
      this.onScroll = this.onScroll.bind(this);
      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onDotsClick = this.onDotsClick.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.prevBtn?.addEventListener('click', this.onPrevClick);
      this.nextBtn?.addEventListener('click', this.onNextClick);
      this.dotsContainer?.addEventListener('click', this.onDotsClick);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      // Dot count depends on how many cards fit per view, so rebuild
      // whenever the track resizes (also fires once on observe).
      this.resizeObserver = new ResizeObserver(() => {
        this.buildDots();
        this.update();
      });
      this.resizeObserver.observe(this.track);

      this.buildDots();
      this.update();
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      this.prevBtn?.removeEventListener('click', this.onPrevClick);
      this.nextBtn?.removeEventListener('click', this.onNextClick);
      this.dotsContainer?.removeEventListener('click', this.onDotsClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.resizeObserver?.disconnect();
    }

    get step() {
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return this.slides[0].getBoundingClientRect().width + gap;
    }

    get maxScroll() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    get scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    // One dot per reachable scroll position (page), not per slide: with
    // several cards in view the last slides can never reach the start edge.
    positionCount() {
      if (this.maxScroll <= 0) return 1;
      return Math.min(this.slides.length, Math.ceil((this.maxScroll - 2) / this.step) + 1);
    }

    currentIndex() {
      const lastIndex = this.positionCount() - 1;
      if (this.maxScroll > 0 && this.track.scrollLeft >= this.maxScroll - 2) {
        return lastIndex;
      }
      return Math.min(lastIndex, Math.round(this.track.scrollLeft / this.step));
    }

    buildDots() {
      if (!this.dotsContainer) return;

      const count = this.positionCount();
      if (count === this.dots.length) return;

      this.dotsContainer.innerHTML = '';
      this.dots = [];
      this.dotsContainer.hidden = count < 2;
      if (count < 2) return;

      for (let i = 0; i < count; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'reasons-why__dot';
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        if (this.track.id) dot.setAttribute('aria-controls', this.track.id);
        this.dots.push(dot);
        this.dotsContainer.appendChild(dot);
      }
    }

    onScroll() {
      if (this.scrollScheduled) return;
      this.scrollScheduled = true;
      requestAnimationFrame(() => {
        this.scrollScheduled = false;
        this.update();
      });
    }

    update() {
      const index = this.currentIndex();

      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });

      if (this.prevBtn) this.prevBtn.disabled = this.track.scrollLeft <= 2;
      if (this.nextBtn) this.nextBtn.disabled = this.track.scrollLeft >= this.maxScroll - 2;
    }

    scrollToIndex(index) {
      const left = Math.min(index * this.step, this.maxScroll);
      this.track.scrollTo({ left: left, behavior: this.scrollBehavior });
    }

    onPrevClick() {
      this.scrollToIndex(Math.max(0, this.currentIndex() - 1));
    }

    onNextClick() {
      this.scrollToIndex(Math.min(this.positionCount() - 1, this.currentIndex() + 1));
    }

    onDotsClick(event) {
      const dot = event.target.closest('.reasons-why__dot');
      if (!dot) return;
      const index = this.dots.indexOf(dot);
      if (index > -1) this.scrollToIndex(index);
    }

    onBlockSelect(event) {
      const slide = event.target.closest?.('.reasons-why__card');
      if (slide && this.slides.includes(slide)) {
        this.scrollToIndex(this.slides.indexOf(slide));
      }
    }
  }

  if (!customElements.get('reasons-why-carousel')) {
    customElements.define('reasons-why-carousel', ReasonsWhyCarousel);
  }
})();
